import { formatDateNoHour, formatHour, getChargingPowerTypeDisplay } from '../../../utilities/generalUtils'
import { useState } from 'react'
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined'
import ChargingHistoryModal from '../../../components/chargingHistory/ChargingHistoryModal'
import { GpCard, GpDivider, Icon } from '../../../components/gp'

// tier1 = Off-Peak, tier2 = Normal, tier3 = Peak  (matches CustomChart)
const TIER_LABELS = {
    1: ['Normal'],
    2: ['Off-Peak', 'Peak'],
    3: ['Off-Peak', 'Normal', 'Peak'],
}
const TOU_COLOR = {
    Peak: 'var(--gp-tou-peak)',
    Normal: 'var(--gp-tou-norm)',
    'Off-Peak': 'var(--gp-tou-off)',
}
const DISPLAY_ORDER = ['Peak', 'Normal', 'Off-Peak']

const roundUpLastTier = (data) => {
    const t1 = Math.round(data?.tier1) || 0
    const t2 = Math.round(data?.tier2) || 0
    const t3 = Math.round(data?.tier3) || 0
    return t1 + t2 + t3 < 100 ? 100 - (t2 + t3) : t1
}

const EnergyCostRows = ({ pricingData }) => {
    const n = Object.keys(pricingData?.L1 || {}).length
    if (n < 1 || !TIER_LABELS[n]) return null
    const rows = TIER_LABELS[n]
        .map((label, k) => ({
            label,
            color: TOU_COLOR[label],
            price: (pricingData?.L2?.[`tier${k + 1}`] || 0) / 100,
        }))
        .sort(
            (a, b) =>
                DISPLAY_ORDER.indexOf(a.label) - DISPLAY_ORDER.indexOf(b.label)
        )

    return (
        <div>
            <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>
                Energy Cost by kWh
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {rows.map((r, i) => (
                    <div key={r.label}>
                        {i > 0 && (
                            <div style={{ height: 1, background: 'var(--gp-hairline)' }} />
                        )}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '8px 0',
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 2,
                                    background: r.color,
                                    flexShrink: 0,
                                }}
                            />
                            <span style={{ font: 'var(--gp-body)', color: 'var(--gp-ink)', flex: 1 }}>
                                {r.label}
                            </span>
                            <span style={{ font: '500 14px/1 var(--gp-mono)', color: 'var(--gp-ink)' }}>
                                {r.price.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                })}
                            </span>
                            <span style={{ font: 'var(--gp-caption)', color: 'var(--gp-ink-mute)' }}>
                                /kWh
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const EnergyUsageBar = ({ data }) => {
    const peak = Math.round(data?.tier3) || 0
    const normal = Math.round(data?.tier2) || 0
    const off = roundUpLastTier(data)
    const seg = (flex, color, val) =>
        flex > 0 ? (
            <div
                style={{
                    flex,
                    background: color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--gp-ink)',
                    font: '500 12px/1 var(--gp-mono)',
                }}
            >
                {flex >= 8 ? `${val}%` : ''}
            </div>
        ) : null
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 10,
                    gap: 8,
                    flexWrap: 'wrap',
                }}
            >
                <span style={{ font: 'var(--gp-body-med)', whiteSpace: 'nowrap' }}>
                    Energy Usage
                </span>
                <span style={{ font: 'var(--gp-caption)', color: 'var(--gp-ink-mute)', whiteSpace: 'nowrap' }}>
                    last 30 days
                </span>
            </div>
            <div
                style={{
                    display: 'flex',
                    height: 28,
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: 'var(--gp-chart-rail)',
                }}
            >
                {seg(peak, 'var(--gp-tou-peak)', peak)}
                {seg(normal, 'var(--gp-tou-norm)', normal)}
                {seg(off, 'var(--gp-tou-off)', off)}
            </div>
        </div>
    )
}

const TouBar = ({ data }) => {
    const peak = Math.round(data?.tier3) || 0
    const normal = Math.round(data?.tier2) || 0
    const off = roundUpLastTier(data)
    return (
        <div
            style={{
                display: 'flex',
                height: 8,
                borderRadius: 999,
                overflow: 'hidden',
                background: 'var(--gp-chart-rail)',
            }}
        >
            {peak > 0 && <div style={{ flex: peak, background: 'var(--gp-tou-peak)' }} />}
            {normal > 0 && <div style={{ flex: normal, background: 'var(--gp-tou-norm)' }} />}
            {off > 0 && <div style={{ flex: off, background: 'var(--gp-tou-off)' }} />}
        </div>
    )
}

const HistoryStat = ({ label, value, align = 'left' }) => (
    <div style={{ textAlign: align }}>
        <div style={{ font: '500 15px/1.1 var(--gp-font)', color: 'var(--gp-ink)' }}>
            {value}
        </div>
        <div
            style={{
                font: '400 10px/1 var(--gp-font)',
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: 'var(--gp-ink-mute)',
                marginTop: 3,
            }}
        >
            {label}
        </div>
    </div>
)

const MySessions = ({
    isRecurring,
    myProperty,
    tierUsagePercentage,
    tierUsagePricing,
    averageChargingPrice,
    chargingSessions,
    navigateToChargingHistory,
}) => {
    const [openHistoryModal, setOpenHistoryModal] = useState(false)
    const [openHistoryModalSessionUUID, setOpenHistoryModalSessionUUID] = useState()
    const [openHistoryModalSessionDate, setOpenHistoryModalSessionDate] = useState()
    const [openHistoryModalSessionPeakWatts, setOpenHistoryModalSessionPeakWatts] = useState()
    const [openHistoryModalSessionPowerOutages, setOpenHistoryModalSessionPowerOutages] = useState([])

    const propertyNotice = () => {
        if (!myProperty || !Object.keys(myProperty).length) return null
        let body = ''
        if (myProperty.userPropertyStatus === 'requested') {
            body = `Your request to access EV charging at ${myProperty.name} is pending approval. You'll receive an email once you've been accepted.`
        } else if (!myProperty.sessionsAllowed) {
            body = `EV charging is not yet available at ${myProperty.name}. Please contact your property manager or support@gopowerev for more information.`
        } else {
            return null
        }
        return (
            <div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        font: 'var(--gp-body-med)',
                        color: 'var(--gp-warn)',
                    }}
                >
                    <WarningOutlinedIcon sx={{ color: 'var(--gp-warn)', fontSize: 20 }} />
                    Property Charging Status
                </div>
                <div
                    style={{
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-ink-mute)',
                        marginTop: 6,
                    }}
                >
                    {body}
                </div>
            </div>
        )
    }

    const notice = propertyNotice()
    const hasUsage = Object.keys(tierUsagePercentage || {}).length !== 0

    return (
        <div className="gp-col">
            <div className="dash-section-title">My Charge Sessions</div>
            <GpCard padding={24}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                    {notice && (
                        <>
                            {notice}
                            <div style={{ height: 1, background: 'var(--gp-hairline)' }} />
                        </>
                    )}

                    {!isRecurring && (
                        <EnergyCostRows pricingData={tierUsagePricing} />
                    )}

                    {hasUsage && (
                        <>
                            <div style={{ height: 1, background: 'var(--gp-hairline)' }} />
                            <EnergyUsageBar data={tierUsagePercentage} />
                        </>
                    )}

                    {!isRecurring && (
                        <>
                            <div style={{ height: 1, background: 'var(--gp-hairline)' }} />
                            <div>
                                <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)', marginBottom: 4 }}>
                                    Average Charging Price
                                </div>
                                {!averageChargingPrice ? (
                                    <div style={{ font: 'var(--gp-body)', color: 'var(--gp-ink-mute)' }}>
                                        No data at this time
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                        <span style={{ font: '500 30px/1 var(--gp-font)', letterSpacing: -0.5, color: 'var(--gp-ink)' }}>
                                            {averageChargingPrice}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <div style={{ height: 1, background: 'var(--gp-hairline)' }} />

                    <div>
                        <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)', marginBottom: 12 }}>
                            Charging History
                        </div>
                        {chargingSessions?.length === 0 ? (
                            <div style={{ font: 'var(--gp-body)', color: 'var(--gp-ink-mute)' }}>
                                No history available
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {chargingSessions?.map((d) => (
                                    <div
                                        key={d.session?.uuid}
                                        onClick={() => {
                                            setOpenHistoryModal(true)
                                            setOpenHistoryModalSessionUUID(d.session?.uuid)
                                            setOpenHistoryModalSessionDate(d.session?.startedAt)
                                            if (d.session?.powerDraw?.peakWatts) {
                                                const peakWatts = parseFloat(d.session.powerDraw.peakWatts / 1000).toFixed(1)
                                                const chargeType = getChargingPowerTypeDisplay(d.session.chargeType, myProperty.powerType, peakWatts)
                                                setOpenHistoryModalSessionPeakWatts(chargeType)
                                            }
                                            if (d?.powerOutages) {
                                                setOpenHistoryModalSessionPowerOutages(d.powerOutages)
                                            }
                                        }}
                                        style={{
                                            border: '1px solid var(--gp-hairline)',
                                            borderRadius: 'var(--gp-r-md)',
                                            overflow: 'hidden',
                                            background: '#FFFFFF',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                background: 'var(--gp-surface)',
                                                borderBottom: '1px solid var(--gp-hairline)',
                                                padding: '8px 14px',
                                                color: 'var(--gp-ink-mute)',
                                                font: '500 12px/1.2 var(--gp-mono)',
                                                letterSpacing: 0.3,
                                            }}
                                        >
                                            <span>{formatDateNoHour(d.session?.startedAt)}</span>
                                            <span>{formatHour(d.session?.startedAt)}</span>
                                        </div>
                                        <div
                                            style={{
                                                padding: '12px 14px 14px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 10,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: isRecurring
                                                        ? '1fr 1fr 1fr'
                                                        : '1fr 1fr 1fr 1fr',
                                                    alignItems: 'baseline',
                                                    gap: 8,
                                                }}
                                            >
                                                <HistoryStat
                                                    label="distance"
                                                    value={`${d.session?.milesCharged ? Math.round(d.session.milesCharged) : 0}mi`}
                                                />
                                                <HistoryStat
                                                    label="duration"
                                                    value={`${d.session?.duration?.hours}h ${d.session?.duration?.minutes}m`}
                                                />
                                                <HistoryStat
                                                    label="energy"
                                                    value={`${d.session?.powerConsumedInKwh?.power}${d.session?.powerConsumedInKwh?.unit}`}
                                                />
                                                {!isRecurring && (
                                                    <HistoryStat
                                                        label="cost"
                                                        value={`$${d.session?.chargingCost}`}
                                                        align="right"
                                                    />
                                                )}
                                            </div>
                                            <TouBar data={d.session?.tierUsagePercentage} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div
                            onClick={navigateToChargingHistory}
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: 14,
                            }}
                        >
                            <span
                                style={{
                                    font: 'var(--gp-body-med)',
                                    color: 'var(--gp-brand)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                See All
                                <Icon name="chevron" size={14} />
                            </span>
                        </div>
                    </div>
                </div>
            </GpCard>

            <ChargingHistoryModal
                open={openHistoryModal}
                sessionDate={openHistoryModalSessionDate}
                sessionUUID={openHistoryModalSessionUUID}
                sessionPeakWatts={openHistoryModalSessionPeakWatts}
                sessionPowerOutages={openHistoryModalSessionPowerOutages}
                handleClose={() => {
                    setOpenHistoryModal(false)
                    setOpenHistoryModalSessionUUID(null)
                    setOpenHistoryModalSessionDate(null)
                    setOpenHistoryModalSessionPeakWatts(null)
                }}
            />
        </div>
    )
}

export default MySessions
